import React from 'react';
import { Save, Loader2, Check, Download, FileText, RefreshCw, X, Pencil } from 'lucide-react';
import { Checkbox } from '@/components/ui/Checkbox';
import { PortalTooltip } from '@/components/ui/PortalTooltip';
import { Button } from '@/components/ui/Button';
import { ATSResumeJson } from '@/types/resume';

interface ResumeToolbarProps {
  isWide: boolean;
  showChanges: boolean;
  setShowChanges: (v: boolean) => void;
  isEditingJson: boolean;
  onToggleEditMode: () => void;
  onCancelEdit: () => void;
  isDownloading: boolean;
  onDownloadPDF: () => void;
  onSaveToExtension: () => void;
  onRetry?: () => void;
}

export const ResumeToolbar: React.FC<ResumeToolbarProps> = ({
  isWide,
  showChanges,
  setShowChanges,
  isEditingJson,
  onToggleEditMode,
  onCancelEdit,
  isDownloading,
  onDownloadPDF,
  onSaveToExtension,
  onRetry
}) => {
  return (
    <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-5 border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 z-40 flex-col md:flex-row gap-4 md:gap-2 rounded-t-xl shrink-0 text-slate-900 dark:text-white">
      <div className="flex items-center justify-between w-full sm:w-auto gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
          <h2 className="text-sm font-semibold tracking-tight w-full sm:w-auto text-center sm:text-left">Tailored Resume</h2>
        </div>
        
        <Checkbox 
          checked={showChanges} 
          onChange={(e) => setShowChanges(e.target.checked)}
          className="sm:ml-4"
        >
          <span className="text-[13px] text-slate-700 dark:text-slate-300 font-medium select-none text-nowrap">Show Highlights</span>
        </Checkbox>
      </div>
      
      <div className="flex items-center justify-center sm:justify-end gap-2 w-full sm:w-auto flex-wrap shrink-0">
        {onRetry && (
          <PortalTooltip disabled={isWide} content="Retry" contentClassName="p-2 w-max text-center max-w-[200px]" className="flex-1 sm:flex-none flex">
            <Button
               variant="ghost"
               onClick={onRetry}
               className="w-full flex-1 sm:flex-none h-auto py-1.5 px-3 min-h-[36px]"
            >
               <RefreshCw className="w-4 h-4 shrink-0" />
               <span className={`whitespace-nowrap ml-1.5 ${isWide ? 'inline' : 'hidden'}`}>Retry</span>
            </Button>
          </PortalTooltip>
        )}
        <PortalTooltip disabled={isWide} content={isEditingJson ? 'Save Changes' : 'Manual Edit'} contentClassName="p-2 w-max text-center max-w-[200px]" className="flex-1 sm:flex-none flex">
          <button
            onClick={onToggleEditMode}
            className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 ${isEditingJson ? 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900 border-transparent dark:shadow-sm' : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 dark:shadow-sm'} border text-sm font-medium rounded-lg transition-all cursor-pointer min-h-[36px]`}
          >
            {isEditingJson ? <Save className="w-4 h-4 shrink-0" /> : <Pencil className="w-4 h-4 shrink-0" />}
            <span className={`whitespace-nowrap ${isWide ? 'inline' : 'hidden'}`}>{isEditingJson ? 'Save Changes' : 'Manual Edit'}</span>
          </button>
        </PortalTooltip>
        {isEditingJson && (
          <PortalTooltip disabled={isWide} content="Cancel" contentClassName="p-2 w-max text-center max-w-[200px]" className="flex-1 sm:flex-none flex">
            <button
              onClick={onCancelEdit}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer min-h-[36px]"
            >
              <X className="w-4 h-4 shrink-0" />
              <span className={`whitespace-nowrap ${isWide ? 'inline' : 'hidden'}`}>Cancel</span>
            </button>
          </PortalTooltip>
        )}
        <PortalTooltip disabled={isWide} content="Export PDF" contentClassName="p-2 w-max text-center max-w-[200px]" className="flex-1 sm:flex-none flex">
          <Button
            variant="primary"
            onClick={onDownloadPDF}
            disabled={isDownloading}
            className="w-full sm:w-auto h-auto py-1.5 px-3 min-h-[36px]"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Download className="w-4 h-4 shrink-0" />}
            <span className={`whitespace-nowrap ml-1.5 ${isWide ? 'inline' : 'hidden'}`}>{isDownloading ? 'Generating...' : 'Export PDF'}</span>
          </Button>
        </PortalTooltip>
        {/* <PortalTooltip disabled={isWide} content="Save for Auto-Fill" contentClassName="p-2 w-max text-center max-w-[200px]" className="flex-1 sm:flex-none flex">
          <button
            onClick={onSaveToExtension}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-50 dark:hover:bg-slate-200 text-white dark:text-slate-900 text-sm font-medium rounded-lg transition-colors cursor-pointer dark:shadow-sm min-h-[36px]"
          >
            <Check className="w-4 h-4 shrink-0" />
            <span className={`whitespace-nowrap ${isWide ? 'inline' : 'hidden'}`}>Save for Auto-Fill</span>
          </button>
        </PortalTooltip> */}
      </div>
    </div>
  );
}
